import React, { useEffect, useState } from 'react'

import { formatClockingDuration, formatClockingSlotsDuration } from '../../utils'
import type { ClockingSlot } from '../../types'

type ClockingTimerProps = {
  start?: string | null
  stop?: string | null
  slots?: ClockingSlot[]
  className?: string
}

type ClockingTickListener = (now: number) => void

const clockingTickListeners = new Set<ClockingTickListener>()
let clockingTickIntervalId: number | null = null
let clockingNow = Date.now()

const startClockingTicker = () => {
  if (clockingTickIntervalId != null) return

  clockingTickIntervalId = window.setInterval(() => {
    clockingNow = Date.now()
    clockingTickListeners.forEach(listener => listener(clockingNow))
  }, 1000)
}

const subscribeToClockingTicker = (listener: ClockingTickListener) => {
  clockingTickListeners.add(listener)
  listener(clockingNow)
  startClockingTicker()

  return () => {
    clockingTickListeners.delete(listener)
    if (clockingTickListeners.size === 0 && clockingTickIntervalId != null) {
      window.clearInterval(clockingTickIntervalId)
      clockingTickIntervalId = null
      clockingNow = Date.now()
    }
  }
}

export default function ClockingTimer({
  start,
  stop,
  slots,
  className,
}: ClockingTimerProps) {
  const [now, setNow] = useState(clockingNow)

  useEffect(() => {
    const hasRunningSlot = slots?.some(slot => !slot.stop)
    if (!hasRunningSlot && (stop || !start)) return

    return subscribeToClockingTicker(setNow)
  }, [slots, start, stop])

  return (
    <span className={className}>
      {slots
        ? formatClockingSlotsDuration(slots, now)
        : formatClockingDuration(start, stop, now)}
    </span>
  )
}
